import { baseApi } from "./baseApi";

export type YardiSyncStatus = "connected" | "error" | "syncing" | "not_tested";

/**
 * Inbound is configured PER COMMUNITY. `inboundPullHour` is the hour in the
 * COMMUNITY'S OWN timezone, not the operator's and not UTC — communities sit in
 * different zones, so a single hour would run mid-afternoon for some of them.
 */
export interface YardiCommunityMapping {
  communityId: string;
  propertyCode: string | null;
  /** V221 — the letter Yardi uses here for a private unit's second occupant.
   *  Documented as varying by operator, so it is configuration, not a constant. */
  secondOccupantDesignator?: "S" | "B";
  inboundEnabled?: boolean;
  inboundPullHour?: number;
  lastInboundPullAt?: string | null;
  lastInboundError?: string | null;
}

/**
 * Row shape returned by the Yardi integration REST surface
 * (server/routes/skycrmYardiIntegrations.ts → mapRowToResponse). Credentials
 * are never returned. There is at most ONE Yardi integration per instance
 * (no integrationType field — unlike ClinicalIntegration, which can be one
 * of several platform types).
 */
export interface YardiIntegration {
  id: string;
  instanceId: string;
  name: string;
  isEnabled: boolean;
  endpointBaseUrl: string | null;
  /** Server-derived: a base URL resolves from the column OR the environment.
   *  False means no Yardi address exists at all and nothing can sync. */
  hasBaseUrl?: boolean;
  syncStatus: YardiSyncStatus;
  syncErrorCode: string | null;
  consecutiveFailures: number;
  lastSuccessfulSyncAt: string | null;
  lastAttemptedSyncAt: string | null;
  /**
   * V215 — when true, SkyCRM refuses edits to the housing-contract fields of a
   * resident already pushed to Yardi, and refuses the lifecycle transitions
   * that write them (move-in, transfer, move-out, reservation-cancel).
   * Integrations created after V215 start FALSE; pre-existing ones were
   * backfilled TRUE so behaviour did not change on deploy.
   */
  createdAt: string;
  updatedAt: string;
  communities: YardiCommunityMapping[];
}

/**
 * One Yardi link row. An entity can hold SEVERAL — the uniques are per
 * (integration, entity), so a person pushed against more than one integration
 * carries one row each, and unlink retires all of them together.
 */
export interface YardiLink {
  id: string;
  integrationId: string;
  status: "pending" | "synced" | "failed" | "unlinked";
  externalId: string | null;
  syncedAt: string | null;
  linkOrigin: "push" | "manual";
  unlinkedAt: string | null;
}

/**
 * There is no `locked` flag any more. The Yardi field lock was ours alone —
 * WelcomeHome permits post-sync edits and simply does not propagate them — so
 * this reports what links EXIST and says nothing about permission.
 */
export interface YardiLinkSet {
  count: number;
  links: YardiLink[];
}

/** One Yardi row the pull could not place onto a record we already have. */
export interface YardiResolutionItem {
  id: string;
  communityId: string;
  communityName: string | null;
  externalId: string;
  propertyCode: string | null;
  payload: Record<string, unknown>;
  status: "unmatched" | "ambiguous";
  firstSeenAt: string;
  lastSeenAt: string;
  seenCount: number;
  /** WelcomeHome parity: a name-based suggestion, dropped when ambiguous.
   *  A shortcut for the operator, never an authority — see the route. */
  suggestedPersonId: string | null;
  suggestedPersonName: string | null;
  /** WHY this row is here, so the operator knows which of the three actions to
   *  reach for rather than defaulting to "Create new resident". */
  reason: "duplicate_profile" | "name_match" | "unknown_person";
  /** For duplicate_profile: the person we ALREADY hold under a different code. */
  conflictPersonName: string | null;
  conflictHeldCode: string | null;
  /** How many synced people share this name. >1 makes the duplicate MORE likely,
   *  not less, so it is reported rather than used to suppress the diagnosis. */
  conflictMatchCount: number | null;
}

/** One entry in a record's Yardi history. */
export interface YardiHistoryEntry {
  kind: "push" | "pull" | "link" | "unlink";
  at: string;
  outcome: string | null;
  actor: string | null;
  externalId: string | null;
  errorCode: string | null;
  changes: { field: string; from: string | null; to: string | null }[];
}

export interface YardiPullResult {
  communityId: string;
  propertyCode: string | null;
  pulled: number;
  applied: number;
  queued: number;
  unparseable: number;
  error?: string;
}

export interface CreateYardiIntegrationRequest {
  name: string;
  credentialToken: string;
  endpointBaseUrl?: string;
  isEnabled?: boolean;
  communities: YardiCommunityMapping[];
}

export interface UpdateYardiIntegrationRequest {
  name?: string;
  isEnabled?: boolean;
  credentialToken?: string;
  endpointBaseUrl?: string | null;
  communities?: YardiCommunityMapping[];
  /** V215 — omit to leave the tenant's current lock setting untouched. */
}

export interface YardiTestConnectionResult {
  ok: boolean;
  message?: string;
  code?: string;
}

/**
 * Outcome of a Yardi push-once sync. A person/resident already carrying a
 * 'synced' skycrm_yardi_sync_state row 409s as
 * ALREADY_SYNCED rather than returning a re-push outcome (no update leg).
 */
export interface YardiSyncOutcome {
  ok: boolean;
  externalId?: string;
  code?: string;
  message?: string;
}

export interface YardiPersonSyncState {
  status: "pending" | "synced" | "failed" | null;
  externalId: string | null;
  syncedAt: string | null;
}

export const yardiIntegrationsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getYardiIntegration: builder.query<
      { integration: YardiIntegration | null },
      { instanceId: string }
    >({
      query: ({ instanceId }) => ({
        url: `/skycrm/yardi-integrations`,
        params: { instanceId },
      }),
      providesTags: (result) =>
        result?.integration
          ? [
              { type: "YardiIntegrations" as const, id: result.integration.id },
              { type: "YardiIntegrations" as const, id: "LIST" },
            ]
          : [{ type: "YardiIntegrations" as const, id: "LIST" }],
    }),

    createYardiIntegration: builder.mutation<
      { integration: YardiIntegration },
      { instanceId: string; data: CreateYardiIntegrationRequest }
    >({
      query: ({ instanceId, data }) => ({
        url: `/skycrm/yardi-integrations`,
        method: "POST",
        params: { instanceId },
        body: data,
      }),
      invalidatesTags: [{ type: "YardiIntegrations", id: "LIST" }],
    }),

    updateYardiIntegration: builder.mutation<
      { integration: YardiIntegration },
      { instanceId: string; id: string; data: UpdateYardiIntegrationRequest }
    >({
      query: ({ instanceId, id, data }) => ({
        url: `/skycrm/yardi-integrations/${id}`,
        method: "PUT",
        params: { instanceId },
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "YardiIntegrations", id },
        { type: "YardiIntegrations", id: "LIST" },
      ],
    }),

    deleteYardiIntegration: builder.mutation<void, { instanceId: string; id: string }>({
      query: ({ instanceId, id }) => ({
        url: `/skycrm/yardi-integrations/${id}`,
        method: "DELETE",
        params: { instanceId },
        responseHandler: (response) =>
          response.status === 204 ? Promise.resolve(null) : response.json(),
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "YardiIntegrations", id },
        { type: "YardiIntegrations", id: "LIST" },
      ],
    }),

    testYardiIntegration: builder.mutation<
      YardiTestConnectionResult,
      { instanceId: string; id: string }
    >({
      query: ({ instanceId, id }) => ({
        url: `/skycrm/yardi-integrations/${id}/test`,
        method: "POST",
        params: { instanceId },
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: "YardiIntegrations", id }],
    }),

    /**
     * Push-once sync of ONE resident to the Yardi integration. 200 → accepted
     * (outcome.ok=true); 502 → partner rejected (outcome.ok=false, surfaced
     * via .data not .error, mirrors syncProspectToIntegration); 409
     * ALREADY_SYNCED / REQUIRED_FIELDS_MISSING surface via .error (caller
     * reads err.data.code).
     */
    syncResidentToYardi: builder.mutation<
      YardiSyncOutcome,
      { instanceId: string; integrationId: string; residentId: string }
    >({
      query: ({ instanceId, integrationId, residentId }) => ({
        url: `/skycrm/yardi-integrations/${integrationId}/sync-resident/${residentId}`,
        method: "POST",
        params: { instanceId },
        validateStatus: (response) => response.status === 200 || response.status === 502,
      }),
      // A push CHANGES the link set, so it must invalidate it — otherwise the
      // header still reads "Sync to Yardi" until a manual refresh, on a record
      // that has just been pushed and will now refuse a second attempt.
      invalidatesTags: (_result, _error, { integrationId, residentId }) => [
        { type: "YardiIntegrations", id: integrationId },
        { type: "YardiIntegrations", id: `links:${residentId}` },
        // Every one of these WRITES a history row, so the Yardi History tab
        // has to refetch. The tag was provided by the history query and never
        // invalidated by anything, so the tab showed a stale story until a full
        // reload — on the surface whose entire job is to be the record.
        { type: "YardiIntegrations", id: `history:${residentId}` },
      ],
    }),

    syncPersonToYardi: builder.mutation<
      YardiSyncOutcome,
      { instanceId: string; integrationId: string; personId: string }
    >({
      query: ({ instanceId, integrationId, personId }) => ({
        url: `/skycrm/yardi-integrations/${integrationId}/sync-person/${personId}`,
        method: "POST",
        params: { instanceId },
        validateStatus: (response) => response.status === 200 || response.status === 502,
      }),
      invalidatesTags: (_result, _error, { integrationId, personId }) => [
        { type: "YardiIntegrations", id: integrationId },
        { type: "YardiIntegrations", id: `person-sync-state:${personId}` },
        { type: "YardiIntegrations", id: `history:${personId}` },
        // V216 — the entity link set is where the header now reads its state
        // from. Without this the chip stays on "Sync to Yardi" until a manual
        // refresh, and the operator's next click is answered ALREADY_SYNCED.
        { type: "YardiIntegrations", id: `links:${personId}` },
      ],
    }),

    /** Latest push-once outcome for this prospect (or status=null if never attempted). */
    getYardiPersonSyncState: builder.query<
      YardiPersonSyncState,
      { instanceId: string; integrationId: string; personId: string }
    >({
      query: ({ instanceId, integrationId, personId }) => ({
        url: `/skycrm/yardi-integrations/${integrationId}/sync-state/person/${personId}`,
        params: { instanceId },
      }),
      providesTags: (_result, _error, { personId }) => [
        { type: "YardiIntegrations", id: `person-sync-state:${personId}` },
      ],
    }),

    /** Every non-failed link for an entity, plus the authoritative locked flag. */
    /** This record's Yardi timeline — the reference's "Yardi History" tab. */
    getYardiHistory: builder.query<
      { entries: YardiHistoryEntry[] },
      { instanceId: string; kind: "person" | "resident"; entityId: string }
    >({
      query: ({ instanceId, kind, entityId }) => ({
        url: `/skycrm/yardi-integrations/history/${kind}/${entityId}`,
        params: { instanceId },
      }),
      providesTags: (_r, _e, { entityId }) => [
        { type: "YardiIntegrations", id: `history:${entityId}` },
      ],
    }),
    getYardiLinks: builder.query<
      YardiLinkSet,
      { instanceId: string; kind: "person" | "resident"; entityId: string }
    >({
      query: ({ instanceId, kind, entityId }) => ({
        url: `/skycrm/yardi-integrations/links/${kind}/${entityId}`,
        params: { instanceId },
      }),
      providesTags: (_r, _e, { entityId }) => [
        { type: "YardiIntegrations", id: `links:${entityId}` },
      ],
    }),

    /** What a push would refuse on, asked BEFORE the click. Yardi's required
     *  fields are unit / move-in date / privacy-level-on-shared — NOT the
     *  clinical partners' name / birthdate / gender. */
    getYardiPushReadiness: builder.query<
      {
        ready: boolean;
        missing: string[];
        deceased: boolean;
        closed: boolean;
        secondOccupantUnresolved: boolean;
      },
      { instanceId: string; kind: "person" | "resident"; entityId: string }
    >({
      query: ({ instanceId, kind, entityId }) => ({
        url: `/skycrm/yardi-integrations/push-readiness/${kind}/${entityId}`,
        params: { instanceId },
      }),
      providesTags: (_r, _e, { entityId }) => [
        { type: "YardiIntegrations", id: `readiness:${entityId}` },
      ],
    }),

    /** Claims stuck mid-push: a crash between claiming and recording the outcome
     *  leaves a 'pending' row that can be neither pushed again nor unlinked.
     *  Distinct from a FAILED push, which is already retryable — nothing reached
     *  Yardi, so there is nothing to duplicate. */
    getYardiStuckClaims: builder.query<
      {
        claims: {
          id: string;
          integrationId: string;
          entityKind: "person" | "resident";
          entityId: string;
          ageMinutes: number;
          lastAttemptAt: string | null;
        }[];
      },
      { instanceId: string }
    >({
      query: ({ instanceId }) => ({
        url: `/skycrm/yardi-integrations/stuck-claims`,
        params: { instanceId },
      }),
      providesTags: [{ type: "YardiIntegrations", id: "STUCK" }],
    }),

    /** Release a stuck claim so the record can be pushed again. Marks it failed —
     *  which is honest: the push never completed, and nothing reached Yardi. */
    abandonYardiClaim: builder.mutation<
      { abandoned: boolean },
      { instanceId: string; kind: "person" | "resident"; entityId: string }
    >({
      query: ({ instanceId, kind, entityId }) => ({
        url: `/skycrm/yardi-integrations/links/${kind}/${entityId}/abandon`,
        method: "POST",
        params: { instanceId },
      }),
      invalidatesTags: (_r, _e, { entityId }) => [
        { type: "YardiIntegrations", id: "STUCK" },
        { type: "YardiIntegrations", id: `links:${entityId}` },
        { type: "YardiIntegrations", id: `history:${entityId}` },
      ],
    }),

    /** What Yardi currently holds for this record — the reference's own
     *  discrepancy check. Read-only: it applies nothing and is not a pull.
     *  `record: null` is a real answer, not an error: Yardi returned the
     *  property but not this code, so the link points at something that is no
     *  longer there. */
    getYardiResidentData: builder.query<
      {
        record: Record<string, unknown> | null;
        externalId: string;
        syncedAt: string | null;
      },
      { instanceId: string; kind: "person" | "resident"; entityId: string }
    >({
      query: ({ instanceId, kind, entityId }) => ({
        url: `/skycrm/yardi-integrations/resident-data/${kind}/${entityId}`,
        params: { instanceId },
      }),
    }),

    /** Retire EVERY live link for the entity. Refuses if a push is in flight. */
    unlinkYardi: builder.mutation<
      { unlinked: number },
      { instanceId: string; kind: "person" | "resident"; entityId: string }
    >({
      query: ({ instanceId, kind, entityId }) => ({
        url: `/skycrm/yardi-integrations/links/${kind}/${entityId}/unlink`,
        method: "POST",
        params: { instanceId },
      }),
      invalidatesTags: (_r, _e, { entityId }) => [
        { type: "YardiIntegrations", id: `links:${entityId}` },
        { type: "YardiIntegrations", id: `person-sync-state:${entityId}` },
        { type: "YardiIntegrations", id: `history:${entityId}` },
      ],
    }),

    /** Record that Yardi already holds this record, without pushing. Admin only. */
    manualLinkYardi: builder.mutation<
      { id: string; externalId: string },
      {
        instanceId: string;
        integrationId: string;
        kind: "person" | "resident";
        entityId: string;
        externalId: string;
      }
    >({
      query: ({ instanceId, integrationId, kind, entityId, externalId }) => ({
        url: `/skycrm/yardi-integrations/${integrationId}/links/${kind}/${entityId}`,
        method: "POST",
        params: { instanceId },
        body: { externalId },
      }),
      invalidatesTags: (_r, _e, { entityId }) => [
        { type: "YardiIntegrations", id: `links:${entityId}` },
        { type: "YardiIntegrations", id: `person-sync-state:${entityId}` },
        { type: "YardiIntegrations", id: `history:${entityId}` },
      ],
    }),

    /** Run the pull for one community now. Same path the nightly sweep takes. */
    pullYardiCommunity: builder.mutation<
      YardiPullResult,
      { instanceId: string; communityId: string }
    >({
      query: ({ instanceId, communityId }) => ({
        url: `/skycrm/yardi-integrations/inbound/community/${communityId}/pull`,
        method: "POST",
        params: { instanceId },
      }),
      invalidatesTags: [
        { type: "YardiIntegrations", id: "LIST" },
        { type: "YardiIntegrations", id: "RESOLUTION" },
      ],
    }),

    getYardiResolutionQueue: builder.query<
      { count: number; items: YardiResolutionItem[] },
      { instanceId: string; limit?: number }
    >({
      query: ({ instanceId, limit }) => ({
        url: `/skycrm/yardi-integrations/inbound/resolution-queue`,
        // `limit` exists so a caller that only needs the COUNT does not pull
        // every open payload. count is the true total either way — the server
        // computes it with a window function, independent of the page size.
        params: limit === undefined ? { instanceId } : { instanceId, limit },
      }),
      // Server returns snake_case rows; normalise at the boundary so no
      // component has to know the wire shape.
      transformResponse: (r: {
        count: number;
        items: Record<string, unknown>[];
      }): { count: number; items: YardiResolutionItem[] } => ({
        count: r.count,
        items: (r.items ?? []).map((i) => ({
          id: String(i.id),
          communityId: String(i.community_id),
          communityName: (i.community_name as string | null) ?? null,
          externalId: String(i.external_id),
          propertyCode: (i.property_code as string | null) ?? null,
          payload: (i.payload as Record<string, unknown>) ?? {},
          status: i.status as "unmatched" | "ambiguous",
          firstSeenAt: String(i.first_seen_at),
          lastSeenAt: String(i.last_seen_at),
          seenCount: Number(i.seen_count ?? 1),
          suggestedPersonId: (i.suggested_person_id as string | null) ?? null,
          suggestedPersonName: (i.suggested_person_name as string | null) ?? null,
          reason: (i.reason as YardiResolutionItem["reason"] | undefined) ?? "unknown_person",
          conflictPersonName: (i.conflict_person_name as string | null) ?? null,
          conflictHeldCode: (i.conflict_held_code as string | null) ?? null,
          conflictMatchCount: (i.conflict_match_count as number | null) ?? null,
        })),
      }),
      providesTags: [{ type: "YardiIntegrations", id: "RESOLUTION" }],
    }),

    /** Link the Yardi row to an existing prospect — a manual link, server-side. */
    matchYardiResolutionItem: builder.mutation<
      { id: string; status: string },
      { instanceId: string; itemId: string; personId: string }
    >({
      query: ({ instanceId, itemId, personId }) => ({
        url: `/skycrm/yardi-integrations/inbound/resolution-queue/${itemId}/match`,
        method: "POST",
        params: { instanceId },
        body: { personId },
      }),
      // RESOLUTION alone left the linked person's own surfaces stale — its link
      // set, its sync state and its history all change here.
      invalidatesTags: (_r, _e, arg) => [
        { type: "YardiIntegrations" as const, id: "RESOLUTION" },
        ...(arg && typeof arg === "object" && "personId" in arg && typeof arg.personId === "string"
          ? [
              { type: "YardiIntegrations" as const, id: `links:${arg.personId}` },
              { type: "YardiIntegrations" as const, id: `person-sync-state:${arg.personId}` },
              { type: "YardiIntegrations" as const, id: `history:${arg.personId}` },
            ]
          : []),
      ],
    }),

    /** Not ours. Recorded rather than deleted, so the sweep stops re-queueing it. */
    /** WelcomeHome's third action: mint a new resident from the Yardi payload
     *  and link it. Refuses (409) rather than inventing a placement — most often
     *  UNIT_NOT_FOUND, which the operator fixes and retries. */
    createYardiResolutionResident: builder.mutation<
      { id: string; status: string; personId?: string },
      { instanceId: string; itemId: string }
    >({
      query: ({ instanceId, itemId }) => ({
        url: `/skycrm/yardi-integrations/inbound/resolution-queue/${itemId}/create`,
        method: "POST",
        params: { instanceId },
      }),
      invalidatesTags: ["YardiIntegrations", "CrmResidents", "CrmPersons"],
    }),

    dismissYardiResolutionItem: builder.mutation<
      { id: string; status: string },
      { instanceId: string; itemId: string }
    >({
      query: ({ instanceId, itemId }) => ({
        url: `/skycrm/yardi-integrations/inbound/resolution-queue/${itemId}/dismiss`,
        method: "POST",
        params: { instanceId },
      }),
      invalidatesTags: [{ type: "YardiIntegrations", id: "RESOLUTION" }],
    }),
  }),
});

export const {
  useGetYardiIntegrationQuery,
  useCreateYardiIntegrationMutation,
  useUpdateYardiIntegrationMutation,
  useDeleteYardiIntegrationMutation,
  useTestYardiIntegrationMutation,
  useSyncResidentToYardiMutation,
  useSyncPersonToYardiMutation,
  useGetYardiPersonSyncStateQuery,
  useGetYardiLinksQuery,
  useGetYardiHistoryQuery,
  useGetYardiPushReadinessQuery,
  useGetYardiStuckClaimsQuery,
  useLazyGetYardiResidentDataQuery,
  useAbandonYardiClaimMutation,
  useUnlinkYardiMutation,
  useManualLinkYardiMutation,
  usePullYardiCommunityMutation,
  useGetYardiResolutionQueueQuery,
  useMatchYardiResolutionItemMutation,
  useDismissYardiResolutionItemMutation,
  useCreateYardiResolutionResidentMutation,
} = yardiIntegrationsApi;
